import { DEFAULT_KDF, NONCE_BYTES, ENVELOPE_VERSION, ENVELOPE_OVERHEAD, SALT_BYTES, KDF_ALG, isSafeKdf, KDF_BOUNDS, KDF_OUTPUT_BYTES, KEY_BYTES, LOGIN_KEY_CONTEXT, RECOVERY_LOGIN_KEY_CONTEXT, RECOVERY_KEY_GROUP, RECOVERY_KEY_PREFIX, RECOVERY_KEY_CHARS, AD } from './chunk-SA27ENP5.js';
import { utf8, toBase64Url, fromBase64Url } from './chunk-LAMR6P2E.js';
import sodiumModule from 'libsodium-wrappers-sumo';

var instance = null;
var pending = null;
function initCrypto() {
  if (instance) return Promise.resolve(instance);
  pending ??= sodiumModule.ready.then(() => {
    instance = sodiumModule;
    return sodiumModule;
  });
  return pending;
}
function isCryptoReady() {
  return instance !== null;
}
function sodium() {
  if (!instance) throw new Error("crypto is not initialized: await initCrypto() first");
  return instance;
}

// src/crypto/errors.ts
var CryptoError = class extends Error {
  constructor(code, message) {
    super(message);
    this.code = code;
  }
  code;
  name = "CryptoError";
};

// src/crypto/envelope.ts
function checkKey(key) {
  if (key.length !== KEY_BYTES)
    throw new CryptoError("invalid_key", `key must be ${KEY_BYTES} bytes`);
}
function randomBytes(n) {
  return sodium().randombytes_buf(n);
}
function encryptEnvelope(key, plaintext, ad, options = {}) {
  checkKey(key);
  const s = sodium();
  const nonce = options.nonce ?? s.randombytes_buf(NONCE_BYTES);
  if (nonce.length !== NONCE_BYTES) throw new CryptoError("malformed", "nonce must be 24 bytes");
  const ct = s.crypto_aead_xchacha20poly1305_ietf_encrypt(plaintext, utf8(ad), null, nonce, key);
  const out = new Uint8Array(1 + NONCE_BYTES + ct.length);
  out[0] = ENVELOPE_VERSION;
  out.set(nonce, 1);
  out.set(ct, 1 + NONCE_BYTES);
  return out;
}
function decryptEnvelope(key, envelope, ad) {
  checkKey(key);
  if (envelope.length < ENVELOPE_OVERHEAD) throw new CryptoError("malformed", "envelope too short");
  if (envelope[0] !== ENVELOPE_VERSION) {
    throw new CryptoError("unsupported_version", `unsupported envelope version ${envelope[0]}`);
  }
  const nonce = envelope.subarray(1, 1 + NONCE_BYTES);
  const ct = envelope.subarray(1 + NONCE_BYTES);
  try {
    return sodium().crypto_aead_xchacha20poly1305_ietf_decrypt(null, ct, utf8(ad), nonce, key);
  } catch {
    throw new CryptoError("decrypt_failed", "decryption failed: wrong key, ad or corrupted data");
  }
}
function encryptToBase64Url(key, plaintext, ad, options) {
  return toBase64Url(encryptEnvelope(key, plaintext, ad, options));
}
function decryptFromBase64Url(key, envelope, ad) {
  let bytes;
  try {
    bytes = fromBase64Url(envelope);
  } catch {
    throw new CryptoError("malformed", "envelope is not valid base64url");
  }
  return decryptEnvelope(key, bytes, ad);
}

// src/crypto/kdf.ts
function generateSalt() {
  return sodium().randombytes_buf(SALT_BYTES);
}
function normalizePassword(password) {
  return utf8(password.normalize("NFKC"));
}
function derivePasswordKeys(password, salt, kdf = DEFAULT_KDF) {
  if (kdf.alg !== KDF_ALG)
    throw new CryptoError("unsafe_kdf", `unsupported KDF ${String(kdf.alg)}`);
  if (!isSafeKdf(kdf)) {
    throw new CryptoError(
      "unsafe_kdf",
      `KDF params out of bounds (ops ${KDF_BOUNDS.minOps}..${KDF_BOUNDS.maxOps}, mem ${KDF_BOUNDS.minMem}..${KDF_BOUNDS.maxMem} bytes)`
    );
  }
  if (salt.length !== SALT_BYTES) throw new CryptoError("malformed", "salt must be 16 bytes");
  const s = sodium();
  const out = s.crypto_pwhash(
    KDF_OUTPUT_BYTES,
    normalizePassword(password),
    salt,
    kdf.ops,
    kdf.mem,
    s.crypto_pwhash_ALG_ARGON2ID13
  );
  const authSeed = out.slice(0, KEY_BYTES);
  const passwordKey = out.slice(KEY_BYTES, 2 * KEY_BYTES);
  const loginKey = s.crypto_generichash(KEY_BYTES, utf8(LOGIN_KEY_CONTEXT), authSeed);
  return { authSeed, loginKey, passwordKey };
}
function deriveRecoveryLoginKey(recoveryKey) {
  if (recoveryKey.length !== KEY_BYTES)
    throw new CryptoError("invalid_key", "recovery key must be 32 bytes");
  return sodium().crypto_generichash(KEY_BYTES, utf8(RECOVERY_LOGIN_KEY_CONTEXT), recoveryKey);
}

// src/crypto/recovery.ts
var CROCKFORD_ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
function encodeCrockford(bytes) {
  let out = "";
  let buffer = 0;
  let bits = 0;
  for (const b of bytes) {
    buffer = buffer << 8 | b;
    bits += 8;
    while (bits >= 5) {
      out += CROCKFORD_ALPHABET[buffer >> bits - 5 & 31];
      bits -= 5;
    }
    buffer &= (1 << bits) - 1;
  }
  if (bits > 0) out += CROCKFORD_ALPHABET[buffer << 5 - bits & 31];
  return out;
}
function crockfordValue(ch) {
  const c = ch.toUpperCase();
  const mapped = c === "O" ? "0" : c === "I" || c === "L" ? "1" : c;
  const v = CROCKFORD_ALPHABET.indexOf(mapped);
  if (v < 0) throw new CryptoError("invalid_recovery_key", `invalid character "${ch}"`);
  return v;
}
function decodeCrockford(text, byteLength) {
  const out = new Uint8Array(byteLength);
  let buffer = 0;
  let bits = 0;
  let o = 0;
  for (const ch of text) {
    buffer = buffer << 5 | crockfordValue(ch);
    bits += 5;
    if (bits >= 8) {
      if (o >= byteLength) throw new CryptoError("invalid_recovery_key", "too many characters");
      out[o++] = buffer >> bits - 8 & 255;
      bits -= 8;
    }
    buffer &= (1 << bits) - 1;
  }
  if (o !== byteLength) throw new CryptoError("invalid_recovery_key", "too few characters");
  if (buffer !== 0) throw new CryptoError("invalid_recovery_key", "non-zero padding bits");
  return out;
}
function generateRecoveryKey() {
  return sodium().randombytes_buf(KEY_BYTES);
}
function formatRecoveryKey(key) {
  if (key.length !== KEY_BYTES)
    throw new CryptoError("invalid_key", "recovery key must be 32 bytes");
  const text = encodeCrockford(key);
  const groups = [];
  for (let i = 0; i < text.length; i += RECOVERY_KEY_GROUP) {
    groups.push(text.slice(i, i + RECOVERY_KEY_GROUP));
  }
  return RECOVERY_KEY_PREFIX + groups.join("-");
}
function parseRecoveryKey(input) {
  let text = input.trim().toUpperCase().replace(/[\s‐-―-]+/g, "-");
  const prefix = RECOVERY_KEY_PREFIX.slice(0, -1);
  if (text.startsWith(prefix + "-")) text = text.slice(prefix.length + 1);
  else if (text.startsWith(prefix) && text.replace(/-/g, "").length === prefix.length + RECOVERY_KEY_CHARS) {
    text = text.slice(prefix.length);
  }
  text = text.replace(/-/g, "");
  if (text.length !== RECOVERY_KEY_CHARS) {
    throw new CryptoError("invalid_recovery_key", `expected ${RECOVERY_KEY_CHARS} characters`);
  }
  return decodeCrockford(text, KEY_BYTES);
}

// src/crypto/keys.ts
function generateKey() {
  return sodium().randombytes_buf(KEY_BYTES);
}
function wrapKey(wrappingKey, key, ad, options) {
  if (key.length !== KEY_BYTES)
    throw new CryptoError("invalid_key", "wrapped key must be 32 bytes");
  return encryptToBase64Url(wrappingKey, key, ad, options);
}
function unwrapKey(wrappingKey, wrapped, ad) {
  const key = decryptFromBase64Url(wrappingKey, wrapped, ad);
  if (key.length !== KEY_BYTES)
    throw new CryptoError("invalid_key", "unwrapped key is not 32 bytes");
  return key;
}
var wrapMasterKeyWithPassword = (masterKey, passwordKey, userId) => wrapKey(passwordKey, masterKey, AD.masterKeyPassword(userId));
var unwrapMasterKeyWithPassword = (wrapped, passwordKey, userId) => unwrapKey(passwordKey, wrapped, AD.masterKeyPassword(userId));
var wrapMasterKeyWithRecovery = (masterKey, recoveryKey, userId) => wrapKey(recoveryKey, masterKey, AD.masterKeyRecovery(userId));
var unwrapMasterKeyWithRecovery = (wrapped, recoveryKey, userId) => unwrapKey(recoveryKey, wrapped, AD.masterKeyRecovery(userId));
var wrapNotebookKey = (notebookKey, masterKey, notebookId) => wrapKey(masterKey, notebookKey, AD.notebookKey(notebookId));
var unwrapNotebookKey = (wrapped, masterKey, notebookId) => unwrapKey(masterKey, wrapped, AD.notebookKey(notebookId));
var wrapNotebookKeyForServer = (notebookKey, serverKey, notebookId) => wrapKey(serverKey, notebookKey, AD.notebookKeyServer(notebookId));
var unwrapNotebookKeyForServer = (wrapped, serverKey, notebookId) => unwrapKey(serverKey, wrapped, AD.notebookKeyServer(notebookId));
var wrapNoteKey = (noteKey, notebookKey, noteId) => wrapKey(notebookKey, noteKey, AD.noteKey(noteId));
var unwrapNoteKey = (wrapped, notebookKey, noteId) => unwrapKey(notebookKey, wrapped, AD.noteKey(noteId));
var rewrapNoteKey = (wrapped, fromNotebookKey, toNotebookKey, noteId) => wrapNoteKey(unwrapNoteKey(wrapped, fromNotebookKey, noteId), toNotebookKey, noteId);
var encryptDocUpdate = (key, docId, update, options) => encryptEnvelope(key, update, AD.doc(docId), options);
var decryptDocUpdate = (key, docId, envelope) => decryptEnvelope(key, envelope, AD.doc(docId));
var encryptAwareness = (key, docId, update, options) => encryptEnvelope(key, update, AD.awareness(docId), options);
var decryptAwareness = (key, docId, envelope) => decryptEnvelope(key, envelope, AD.awareness(docId));
function createAccountKeys(password, userId, kdf = DEFAULT_KDF) {
  const salt = generateSalt();
  const { loginKey, passwordKey } = derivePasswordKeys(password, salt, kdf);
  const masterKey = generateKey();
  const recoveryKey = generateRecoveryKey();
  return {
    salt,
    kdf,
    loginKey,
    passwordKey,
    masterKey,
    recoveryKey,
    recoveryKeyText: formatRecoveryKey(recoveryKey),
    recoveryLoginKey: deriveRecoveryLoginKey(recoveryKey),
    wrappedMasterKeyPassword: wrapMasterKeyWithPassword(masterKey, passwordKey, userId),
    wrappedMasterKeyRecovery: wrapMasterKeyWithRecovery(masterKey, recoveryKey, userId)
  };
}
function rekeyPassword(masterKey, newPassword, userId, kdf = DEFAULT_KDF) {
  const salt = generateSalt();
  const { loginKey, passwordKey } = derivePasswordKeys(newPassword, salt, kdf);
  return {
    salt,
    kdf,
    loginKey,
    passwordKey,
    wrappedMasterKeyPassword: wrapMasterKeyWithPassword(masterKey, passwordKey, userId)
  };
}
function rotateRecoveryKey(masterKey, currentLoginKey, userId, options = {}) {
  const recoveryKey = options.recoveryKey ?? generateRecoveryKey();
  return {
    recoveryKey,
    recoveryKeyText: formatRecoveryKey(recoveryKey),
    body: {
      currentLoginKey: toBase64Url(currentLoginKey),
      recoveryLoginKey: toBase64Url(deriveRecoveryLoginKey(recoveryKey)),
      wrappedMasterKeyRecovery: wrapKey(
        recoveryKey,
        masterKey,
        AD.masterKeyRecovery(userId),
        options.nonce ? { nonce: options.nonce } : void 0
      )
    }
  };
}
function signupFields(keys) {
  return {
    salt: toBase64Url(keys.salt),
    kdf: keys.kdf,
    loginKey: toBase64Url(keys.loginKey),
    recoveryLoginKey: toBase64Url(keys.recoveryLoginKey),
    wrappedMasterKeyPassword: keys.wrappedMasterKeyPassword,
    wrappedMasterKeyRecovery: keys.wrappedMasterKeyRecovery
  };
}

export { CROCKFORD_ALPHABET, CryptoError, createAccountKeys, decodeCrockford, decryptAwareness, decryptDocUpdate, decryptEnvelope, decryptFromBase64Url, derivePasswordKeys, deriveRecoveryLoginKey, encodeCrockford, encryptAwareness, encryptDocUpdate, encryptEnvelope, encryptToBase64Url, formatRecoveryKey, generateKey, generateRecoveryKey, generateSalt, initCrypto, isCryptoReady, normalizePassword, parseRecoveryKey, randomBytes, rekeyPassword, rewrapNoteKey, rotateRecoveryKey, signupFields, sodium, unwrapKey, unwrapMasterKeyWithPassword, unwrapMasterKeyWithRecovery, unwrapNoteKey, unwrapNotebookKey, unwrapNotebookKeyForServer, wrapKey, wrapMasterKeyWithPassword, wrapMasterKeyWithRecovery, wrapNoteKey, wrapNotebookKey, wrapNotebookKeyForServer };
//# sourceMappingURL=chunk-PXX4LS7M.js.map
//# sourceMappingURL=chunk-PXX4LS7M.js.map